import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../api/client';

interface Toast { id: number; text: string; error: boolean }
interface ToastApi { show: (text: string) => void; error: (e: unknown) => void }
const Ctx = createContext<ToastApi>({ show: () => {}, error: () => {} });

let seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const { t } = useTranslation();
  const push = useCallback((text: string, error: boolean) => {
    const id = ++seq;
    setToasts((x) => [...x.slice(-3), { id, text, error }]);
    setTimeout(() => setToasts((x) => x.filter((y) => y.id !== id)), error ? 6000 : 3000);
  }, []);
  const value = useMemo<ToastApi>(() => ({
    show: (text) => push(text, false),
    error: (e) => push(errorText(e, t), true),
  }), [push, t]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((x) => <div key={x.id} className={`toast${x.error ? ' err' : ''}`}>{x.text}</div>)}
      </div>
    </Ctx.Provider>
  );
}

export const useToast = () => useContext(Ctx);

/** Fehlermeldung in Klartext; bekannte Codes übersetzt, sonst Titel vom Server. */
export function errorText(e: unknown, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (e instanceof ApiError) {
    const key = `errors.${e.code}`;
    const tr = t(key);
    const base = tr !== key ? tr : e.message;
    const details = e.problem.errors ? Object.values(e.problem.errors).flat().filter(Boolean) : [];
    return details.length && e.code !== 'validation_failed' ? `${base} (${details.join(', ')})` : details.length ? `${base}: ${details.join(', ')}` : base;
  }
  return e instanceof Error ? e.message : String(e);
}
